import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useCatAssistant } from '../contexts/CatAssistantContext';

interface Reservation {
    id: string;
    customerName: string;
    startDate: string;
    board: {
        name: string;
    };
}

export const useReservationReminders = () => {
    const { say } = useCatAssistant();
    const [upcomingReservations, setUpcomingReservations] = useState<Reservation[]>([]);
    const announcedReservations = useRef<Set<string>>(new Set());

    // Fetch upcoming reservations every 5 minutes
    useEffect(() => {
        const fetchUpcoming = async () => {
            try {
                const response = await axios.get('/api/dashboard/upcoming');
                setUpcomingReservations(response.data);
            } catch (error) {
                console.error('Error fetching upcoming reservations:', error);
            }
        };

        fetchUpcoming();
        const interval = setInterval(fetchUpcoming, 5 * 60 * 1000); // 5 minutes

        return () => clearInterval(interval);
    }, []);

    // Check for reminders every minute
    useEffect(() => {
        const checkReminders = () => {
            const now = new Date();

            upcomingReservations.forEach(reservation => {
                const start = new Date(reservation.startDate);
                const diffMs = start.getTime() - now.getTime();
                const diffMins = Math.round(diffMs / 60000);

                // Announce 60 minutes before
                if (diffMins === 60 && !announcedReservations.current.has(`${reservation.id}_60`)) {
                    say(`¡Atención! ⏰ Tienes un turno con ${reservation.customerName} en ${reservation.board.name} en 1 hora.`, 'alert');
                    announcedReservations.current.add(`${reservation.id}_60`);
                }

                // Announce 15 minutes before
                if (diffMins === 15 && !announcedReservations.current.has(`${reservation.id}_15`)) {
                    say(`¡Atención! ⏰ Tienes un turno con ${reservation.customerName} en ${reservation.board.name} en 15 minutos.`, 'alert');
                    announcedReservations.current.add(`${reservation.id}_15`);
                }

                // Announce 5 minutes before
                if (diffMins === 5 && !announcedReservations.current.has(`${reservation.id}_5`)) {
                    say(`¡Prepárate! 😺 ${reservation.customerName} llega en 5 minutos a ${reservation.board.name}.`, 'tip');
                    announcedReservations.current.add(`${reservation.id}_5`);
                }
            });
        };

        const interval = setInterval(checkReminders, 60 * 1000); // 1 minute

        // Initial check
        checkReminders();

        return () => clearInterval(interval);
    }, [upcomingReservations, say]);
};
